import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SignpostBig, Trash2, Plus, MapPin, Route } from "lucide-react";

const STATUS = [
  { value: "requested", label: "Requested", cls: "bg-amber-100 text-amber-900" },
  { value: "approved", label: "Approved", cls: "bg-sky-100 text-sky-900" },
  { value: "delivered", label: "Delivered", cls: "bg-emerald-100 text-emerald-900" },
  { value: "picked_up", label: "Picked up", cls: "bg-emerald-50 text-emerald-800" },
  { value: "removed", label: "Removed", cls: "bg-slate-200 text-slate-700" },
];

const empty = { requester_name: "", address: "", town: "", contact: "", num_signs: 1, request_date: "", status: "requested", notes: "" };

export default function LawnSigns() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("any");
  const [townFilter, setTownFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [routeMode, setRouteMode] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "any") params.set("status", statusFilter);
      if (townFilter.trim()) params.set("town", townFilter.trim());
      const url = routeMode ? "/lawn-signs/route" : `/lawn-signs?${params.toString()}`;
      const { data } = await api.get(url);
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [routeMode]);

  const openNew = () => { setForm(empty); setEditing({}); };
  const openEdit = (v) => { setForm({ ...empty, ...v, num_signs: v.num_signs || 1 }); setEditing(v); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.requester_name.trim() || !form.address.trim()) { toast.error("Name and address are required"); return; }
    try {
      const payload = { ...form, num_signs: parseInt(form.num_signs) || 1 };
      delete payload.id; delete payload.user_id; delete payload.created_at; delete payload.updated_at;
      if (editing?.id) {
        await api.patch(`/lawn-signs/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/lawn-signs", payload);
        toast.success("Request logged");
      }
      close(); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const updateStatus = async (v, status) => {
    try {
      const payload = { ...v, status };
      delete payload.id; delete payload.user_id; delete payload.created_at; delete payload.updated_at;
      await api.patch(`/lawn-signs/${v.id}`, payload);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete request from ${v.requester_name}?`)) return;
    try {
      await api.delete(`/lawn-signs/${v.id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-flag-red flex items-center gap-2">
            <SignpostBig className="h-4 w-4" /> Lawn Sign Tracker
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1" data-testid="signs-title">Yard sign requests</h1>
          <p className="text-slate-600 mt-2">Log requests, mark deliveries, and pull a route list for drop-off days.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={routeMode ? "default" : "outline"} onClick={() => setRouteMode((v) => !v)} data-testid="signs-route-toggle" className={routeMode ? "h-10 bg-flag-navy text-white font-bold" : "h-10 border-2 font-semibold"}>
            <Route className="h-4 w-4 mr-1.5" /> {routeMode ? "Showing route" : "Build delivery route"}
          </Button>
          <Button onClick={openNew} data-testid="signs-new-btn" className="h-10 bg-flag-red hover:bg-flag-red-hover text-white font-bold"><Plus className="h-4 w-4 mr-1.5" /> Log request</Button>
        </div>
      </div>

      {!routeMode && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3 mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 h-10" data-testid="signs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any status</SelectItem>
              {STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={townFilter} onChange={(e) => setTownFilter(e.target.value)} placeholder="Town contains…" className="h-10 w-56" data-testid="signs-town-filter" />
          <Button onClick={load} variant="outline" className="h-10 border-2 font-semibold">Apply</Button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="signs-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Requester</th>
                <th className="text-left px-3 py-3 font-bold">Address</th>
                <th className="text-left px-3 py-3 font-bold">#</th>
                <th className="text-left px-3 py-3 font-bold">Requested</th>
                <th className="text-left px-3 py-3 font-bold">Status</th>
                <th className="text-right px-3 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-6 text-slate-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-500" data-testid="signs-empty">{routeMode ? "No pending deliveries." : "No requests yet."}</td></tr>}
              {!loading && rows.map((v) => {
                const s = STATUS.find((x) => x.value === v.status) || STATUS[0];
                return (
                  <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`sign-row-${v.id}`}>
                    <td className="px-4 py-3 font-semibold text-flag-navy">{v.requester_name}<div className="text-xs text-slate-500">{v.contact}</div></td>
                    <td className="px-3 py-3 text-slate-700"><div className="flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5 text-slate-400" /><span>{v.address}{v.town ? `, ${v.town}` : ""}</span></div></td>
                    <td className="px-3 py-3 text-slate-700 font-semibold">{v.num_signs}</td>
                    <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">{v.request_date || "—"}</td>
                    <td className="px-3 py-3">
                      <Select value={v.status} onValueChange={(nv) => updateStatus(v, nv)}>
                        <SelectTrigger data-testid={`sign-status-${v.id}`} className={`h-8 text-xs font-semibold border-transparent ${s.cls}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <Button variant="outline" onClick={() => openEdit(v)} data-testid={`sign-edit-${v.id}`} className="h-8 px-2 text-xs border-2 font-semibold">Edit</Button>
                      <Button variant="ghost" onClick={() => remove(v)} data-testid={`sign-delete-${v.id}`} className="h-8 px-2 text-xs text-flag-red ml-1"><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg" data-testid="sign-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit lawn sign request" : "Log a request"}</DialogTitle>
            <DialogDescription>Requester address stays private to your account.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Requester name *" full><Input value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} data-testid="sign-form-name" className="h-10" /></Field>
            <Field label="Address *" full><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="sign-form-address" className="h-10" /></Field>
            <Field label="Town"><Input value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} data-testid="sign-form-town" className="h-10" /></Field>
            <Field label="Contact (phone or email)"><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="h-10" /></Field>
            <Field label="# of signs"><Input type="number" min="1" max="99" value={form.num_signs} onChange={(e) => setForm({ ...form, num_signs: e.target.value })} data-testid="sign-form-num" className="h-10" /></Field>
            <Field label="Request date">
              <Input type="date" value={form.request_date} onChange={(e) => setForm({ ...form, request_date: e.target.value })} className="h-10" />
            </Field>
            <Field label="Status" full>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Notes" full><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} className="border-2 font-semibold">Cancel</Button>
            <Button onClick={save} data-testid="sign-form-save" className="bg-flag-red hover:bg-flag-red-hover text-white font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
